import React, {useState} from 'react'
import {ControlButton} from './ControlButton/index.ts'
import {T} from '@colonist/ui'
import {type ReplayHighlightData, ReplaySlider} from './ReplaySlider/index.ts'
import {AvatarList} from './AvatarList/index.ts'
import styles from './ReplayControlsContent.module.scss'
import clsx from 'clsx'
import IconArrowUp from '@static/images/icon_arrow_up.svg'
import IconArrowDown from '@static/images/icon_arrow_down.svg'
import {ReplayControlsShareButton} from './ShareButton/ReplayControlsShareButton.tsx'
import Icon05x from '@static/images/icon_05x.svg'
import Icon1x from '@static/images/icon_1x.svg'
import Icon2x from '@static/images/icon_2x.svg'
import Icon4x from '@static/images/icon_4x.svg'
import Icon8x from '@static/images/icon_8x.svg'
import IconRewindStart from '@static/images/icon_rewind_start.svg'
import IconRewind from '@static/images/icon_rewind.svg'
import IconPause from '@static/images/icon_pause.svg'
import IconPlay from '@static/images/icon_play.svg'
import IconFastForward from '@static/images/icon_forward.svg'
import IconFastForwardToEnd from '@static/images/icon_forward_end.svg'
import {type GameClientUserState} from '@colonist/game-base-types'
import {type PlayerColor} from '@colonist/game-states'
import {type ImageURL} from '@colonist/types'

export type PlaybackSpeed = '05x' | '1x' | '2x' | '4x' | '8x'
const ORDERED_PLAYBACK_SPEEDS: PlaybackSpeed[] = ['1x', '2x', '4x', '8x', '05x']

const PLAYBACK_SPEED_ICON: Record<PlaybackSpeed, ImageURL> = {
    '05x': Icon05x,
    '1x': Icon1x,
    '2x': Icon2x,
    '4x': Icon4x,
    '8x': Icon8x,
}

export interface UIGameReplayControlsContentProps {
    playbackSpeed: PlaybackSpeed
    onChangePlaybackSpeed: (playbackSpeed: PlaybackSpeed) => void
    isPlaying: boolean
    onTogglePlayback: VoidFunction
    onPreviousTurn: VoidFunction
    onPreviousAction: VoidFunction
    onNextAction: VoidFunction
    onNextTurn: VoidFunction
    onShare?: VoidFunction
    currentPlayerPerspective: PlayerColor
    playerUserStates: GameClientUserState[]
    onChangePerspective: (color: PlayerColor) => void
    numberOfActions: number
    currentActionIndex: number
    onSliderChangeActionIndex: (actionIndex: number) => void
    highlightData: ReplayHighlightData[]
    className?: string
    disableTooltips?: boolean | undefined
}

export const ReplayControlsContent = ({
    playbackSpeed,
    onChangePlaybackSpeed,
    isPlaying,
    onTogglePlayback,
    onPreviousAction,
    onNextAction,
    onPreviousTurn,
    onNextTurn,
    onShare,
    currentPlayerPerspective,
    playerUserStates,
    onChangePerspective,
    numberOfActions,
    currentActionIndex,
    onSliderChangeActionIndex,
    highlightData,
    className,
    disableTooltips,
}: UIGameReplayControlsContentProps) => {
    const [isExpanded, setIsExpanded] = useState(true)

    const handleSpeedChange = (): void => {
        const currentIndex = ORDERED_PLAYBACK_SPEEDS.indexOf(playbackSpeed)
        const nextIndex = (currentIndex + 1) % ORDERED_PLAYBACK_SPEEDS.length
        onChangePlaybackSpeed(ORDERED_PLAYBACK_SPEEDS[nextIndex])
    }

    const toggleExpand = (): void => setIsExpanded(!isExpanded)

    const hasHighlightData = highlightData.length > 0
    const isFirstAction = currentActionIndex <= -1
    return (
        <div className={clsx(styles.replayControlsContainer, className)}>
            <div className={styles.controllerButtons}>
                <ControlButton
                    icon={IconRewindStart}
                    alt="strings:imgAlts.RewindToStart"
                    disabled={isFirstAction}
                    onClick={onPreviousTurn}
                    tooltipContent={<T id="strings:game.tooltips.replay.previousTurn" />}
                    disableTooltip={disableTooltips}
                />
                <ControlButton
                    icon={IconRewind}
                    alt="strings:imgAlts.Rewind"
                    disabled={isFirstAction}
                    onClick={onPreviousAction}
                    tooltipContent={<T id="strings:game.tooltips.replay.previousAction" />}
                    disableTooltip={disableTooltips}
                />
                {
                    isPlaying ? (
                        <ControlButton
                            icon={IconPause}
                            alt="strings:imgAlts.Pause"
                            onClick={onTogglePlayback}
                            tooltipContent={<T id="strings:game.tooltips.replay.playPause" />}
                            disableTooltip={disableTooltips}
                        />
                    ) : (
                        <ControlButton
                            icon={IconPlay}
                            alt="strings:imgAlts.Play"
                            onClick={onTogglePlayback}
                            tooltipContent={<T id="strings:game.tooltips.replay.playPause" />}
                            disableTooltip={disableTooltips}
                        />
                    )
                }
                <ControlButton
                    icon={PLAYBACK_SPEED_ICON[playbackSpeed]}
                    alt="strings:imgAlts.Play"
                    onClick={handleSpeedChange}
                    tooltipContent={<T id="strings:game.tooltips.replay.velocity" />}
                    disableTooltip={disableTooltips}
                />
                <ControlButton
                    icon={IconFastForward}
                    alt="strings:imgAlts.FastForward"
                    onClick={onNextAction}
                    tooltipContent={<T id="strings:game.tooltips.replay.nextAction" />}
                    disableTooltip={disableTooltips}
                />
                <ControlButton
                    icon={IconFastForwardToEnd}
                    alt="strings:imgAlts.FastForwardToEnd"
                    onClick={onNextTurn}
                    tooltipContent={<T id="strings:game.tooltips.replay.nextTurn" />}
                    disableTooltip={disableTooltips}
                />
                <img
                    className={styles.expandIcon}
                    onClick={toggleExpand}
                    src={isExpanded ? IconArrowUp : IconArrowDown}
                />
            </div>

            <ReplaySlider
                highlightData={highlightData}
                max={numberOfActions}
                onChange={onSliderChangeActionIndex}
                shouldShowHighlights={hasHighlightData && isExpanded}
                value={currentActionIndex}
            />

            <div className={clsx(styles.bottomMenu, {[styles.bottomMenuVisible]: isExpanded})}>
                <AvatarList
                    currentPlayerPerspective={currentPlayerPerspective}
                    onChangePerspective={onChangePerspective}
                    playerUserStates={playerUserStates}
                />
                <ReplayControlsShareButton onShare={onShare} />
            </div>
        </div>
    )
}
